import React, { useState, createContext, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { observable, reaction, comparer } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Router } from 'director';
import queryString from 'query-string';

import CircleSpinner from '../components/CircleSpinner';
import hasSameValues from '../functions/hasSameValues';
import noticeError from '../functions/noticeError';
import noticeErrorSaveEvent from '../functions/noticeErrorSaveEvent';


const Context = createContext();

function Navigation({
    isRoutingEnabled,
    // onLoad is expected to run first to set isReady
    onLoad,

    baseRoute,
    activePageName,
    fallbackPageName,
    queryParams,
    children,
}) {
    const state = useNavigationState({
        onLoad,
        baseRoute,
        activePageName,
        fallbackPageName,
        isRoutingEnabled,
        queryParams,
    });

    return (
        <Context.Provider value={state}>
            <Choose>
                <When condition={!state.isReady}>
                    <CircleSpinner />
                </When>
                <Otherwise>
                    {children}
                </Otherwise>
            </Choose>
        </Context.Provider>
    );
}

Navigation.propTypes = {
    onLoad: PropTypes.func,

    baseRoute: PropTypes.string.isRequired,
    activePageName: PropTypes.string,
    fallbackPageName: PropTypes.string,
    queryParams: PropTypes.object,

    isRoutingEnabled: PropTypes.bool,

    // Not required as children could be waiting to render after the async onLoad is complete
    children: PropTypes.node,
};

Navigation.defaultProps = {
    onLoad: undefined,

    activePageName: undefined,
    fallbackPageName: undefined,
    queryParams: undefined,

    isRoutingEnabled: true,

    children: undefined,
};

function useNavigationState({
    baseRoute,
    activePageName,
    fallbackPageName,
    isRoutingEnabled,
    queryParams,
    onLoad,
}) {
    const [ state ] = useState(() => observable({
        // Assumption: baseRoute & isRoutingEnabled will not change during the component's lifecycle
        baseRoute,
        isRoutingEnabled,
        fallbackPageName,

        isReady: !(typeof onLoad === 'function'), // false to wait for onLoad to resolve

        pages: [],
        activePageId: undefined,
        routeParams: undefined,

        get activePage() {
            return state.getPageById(state.activePageId);
        },

        addPage(page) {
            if (page?.id == null || page?.route == null) {
                throw new Error('Pages must have both an id and a route.');
            }
            else if (state.pages.some(existingPage => existingPage.id === page.id || existingPage.route === page.route)) {
                throw new RangeError(`Page ids and routes must be unique. We already contain page with either an id of ${page.id} or with the route ${page.route}`);
            }

            state.pages.push(page);
        },
        removePage(id) {
            const pageIndex = state.pages.findIndex(page => page.id === id);

            // findIndex returns -1 if it cannot find the item
            if (pageIndex === -1) {
                throw new RangeError(`Unable to remove page since we cannot find page with id of ${id}`);
            }

            state.pages.splice(pageIndex, 1);
        },

        getPageById(id) {
            return state.pages.find(page => page.id === id);
        },

        setActivePage(id, routeParams) {
            const page = state.getPageById(id);

            if (!page) {
                throw new RangeError(`Cannot find page with id ${id}`);
            }

            state.activePageId = id;
            state.routeParams = routeParams;

            // Knowing where the user has been can really help with debugging
            noticeErrorSaveEvent(`<Navigation>.setActivePage changed to "${id}"`);
        },

    }));

    // Always run onLoad first because isReady is dependent on it
    useEffect(() => {
        async function handleLoad() {
            // If available, do not invoke onLoad more than once.
            if (state.isReady || typeof onLoad !== 'function') {
                return;
            }

            const relativePath = isRoutingEnabled ? window.location.pathname.replace(state.baseRoute, '') : '';

            try {
                await onLoad(isRoutingEnabled
                    ? {
                        // raw URL 
                        host: window.location.host,
                        href: window.location.href,
                        pathname: window.location.pathname,
                        search: window.location.search,
    
                        queryParams: queryString.parse(window.location.search),
                        relativePath: relativePath,
                        // '/foo/bar' => [ 'foo', 'bar' ]
                        relativeParams: relativePath.split('/').filter(param => param !== ''),

                        isDebug:  queryString.parse(window.location.search)[DEBUG_QUERY_PARAM_KEY] === 'true',
                    }
                    : {},
                );

                state.isReady = true;
            } catch (originalError) {
                noticeError(originalError, {
                    message: `[Navigation.onLoad] ${originalError.message}`,
                });

                state.isReady = false;
            }
        }
        
        handleLoad();
    }, []);

    // When the activePageName prop changes, we want to update our state and the router
    useEffect(() => {
        // isReady will be true before we render the NavigationPage components, so wait until we have pages too or it'll throw.
        if (!activePageName || !state.isReady || !state.pages.length) {
            return;
        }

        state.setActivePage(activePageName);
    }, [ activePageName, state, state.isReady ]);

    // When the isReady is true, we want to set the queryParams
    useEffect(() => {
        if (!isRoutingEnabled
            || !queryParams
            || hasSameValues(queryParams, queryString.parse(search))
            || !state.isReady) {
            return;
        }

        const { pathname, search } = window.location;
        const updatedSearch = queryString.stringify({ ...queryString.parse(search), ...queryParams });

        // Add our newly constructed url to the browser history
        if (search !== updatedSearch) {
            window.history.replaceState(
                null,
                '',
                updatedSearch 
                    ? `${pathname}?${updatedSearch}`
                    : pathname,
            );
        }
    }, [ JSON.stringify(queryParams), state.isReady ]);

    // Ensure fallbackPageName stays synchronized with our state.
    useEffect(() => {
        if (!fallbackPageName) {
            return;
        }

        state.fallbackPageName = fallbackPageName;
    }, [ fallbackPageName, state ]);

    // When the pages are initialized and prop isReady is true we want to set up the route   
    useEffect(() => {
        if (!isRoutingEnabled || !state.isReady) {
            return;
        }

        // state.fallbackPageName should should always be define
        // before state.isReady triggers current usedEffect
        if (!state.fallbackPageName) {
            noticeError(
                new Error('[Navigation] No fallback page is found, expected fallbackPageName to be defined.'),
                { ...state },
            );
        }

        let router;
        const reactionDisposer = reaction(
            () => state.pages.map(page => ({
                id: page.id,
                route: page.route,
            })),
            () => {
                router?.destroy();
                router = initializeRouter(state);
            },
            {
                name: 'Navigation.initializeRouterReaction',
                fireImmediately: true,
                equals: comparer.structural,
            },
        );


        return () => {
            // NOTE: the destroy function doesn't seem to be available when we run automated tests.
            //       the director package seems to alter their API if you're not in the browser....
            router?.destroy?.();
            reactionDisposer();
        };
    }, [ state, state.isReady, isRoutingEnabled ]);

    return state;
}


function initializeRouter(state) {
    const routes = {
        [state.baseRoute]: {
            ...(state.pages.reduce((pageRoutes, page) => ({
                ...pageRoutes,
                [page.route]: {
                    on: (...params) => {
                        let routeParams;

                        // The library we use returns the route params as parameters from the `on` function
                        //  as individual values, we want these to be objects and to use the key supplied by
                        //  the page's route.
                        if (params.length > 0) {
                            const paramKeys = getRouteParamKeysFromRoute(page.route);

                            // We want to create an object where the key is provided from the route and
                            //  the values are provided by this functions parameters
                            routeParams = paramKeys.reduce((combinedParams, currentKey, index) => {
                                combinedParams[currentKey] = params[index];
                                return combinedParams;
                            }, {});
                        }

                        state.setActivePage(page.id, routeParams);
                    },
                },
            }), {})),
        },
    };

    const router = new Router(routes)
        .configure({
            notfound() {
                state.setActivePage(state.fallbackPageName);
            },
            html5history: true,
        });

    // When this is being run on the server, this function will NOT exist!
    // This was added to handle cases where the window check seems to pass,
    // but the function is still not available ....
    if (typeof router.init === 'function') {
        router.init();
    }

    return router;
}

// Takes a route like /:quoteId and returns an array of the 
//  keys like 'quoteId' used by route params for further parsing
function getRouteParamKeysFromRoute(route) {
    const regex = /:(?<param>[\w-]+)/g;

    // Older browsers do not support String.matchAll
    if (typeof route.matchAll === 'function') {
        const paramKeysFromRoute = route.matchAll(regex);

        // matchAll returns type RegExpStringIterator which we need to cast into an array
        //  and get the keys from the group
        return Array.from(paramKeysFromRoute).map(param => param.groups.param);
    }
    
    // For older browsers that do not support matchAll we have to use Regex.exec.
    //  Instead of returning an array of matches like matchAll, it returns the current match
    //  and remembers its index so it can be invoked multiple times to get all the matches
    const paramKeys = [];
    let currentParamMatch = regex.exec(route);

    while (currentParamMatch !== null) {
        paramKeys.push(currentParamMatch.groups.param);
        currentParamMatch = regex.exec(route);
    }
    
    return paramKeys;
}

function useNavigation() {
    return useContext(Context);
}

const DEBUG_QUERY_PARAM_KEY = 'debug';

export default observer(Navigation);
export { useNavigation };
